import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Footer from '../Footer';
import { API_KEY, API_ENDPOINTS, LANGUAGE, IMAGE_BASE_URL } from '../../config/api';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './FilmDetails.css';

const FilmDetails = () => {
    const params = useParams();
    const [movie, setMovie] = useState(null);
    const [videos, setVideos] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [cast, setCast] = useState([]);
    const [crew, setCrew] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVideo, setSelectedVideo] = useState(null);

    useEffect(() => {
        const fetchMovieData = async () => {
            try {
                setLoading(true);
                const [movieData, videosData, recommendationsData, creditsData] = await Promise.all([
                    axios.get(`${API_ENDPOINTS.MOVIE_DETAILS(params.id)}?api_key=${API_KEY}&language=${LANGUAGE}`),
                    axios.get(`${API_ENDPOINTS.MOVIE_VIDEOS(params.id)}?api_key=${API_KEY}&language=${LANGUAGE}`),
                    axios.get(`${API_ENDPOINTS.MOVIE_RECOMMENDATIONS(params.id)}?api_key=${API_KEY}&language=${LANGUAGE}`),
                    axios.get(`${API_ENDPOINTS.MOVIE_CREDITS(params.id)}?api_key=${API_KEY}&language=${LANGUAGE}`)
                ]);
                
                setMovie(movieData.data);
                setVideos(videosData.data.results.filter(video => video.site === 'YouTube' && video.type === 'Trailer'));
                setRecommendations(recommendationsData.data.results.slice(0, 10));
                setCast(creditsData.data.cast.slice(0, 12));
                setCrew(creditsData.data.crew.filter(person => 
                    ['Director', 'Producer', 'Screenplay', 'Writer', 'Editor', 'Cinematography'].includes(person.job)
                ));
            } catch (error) {
                console.error('Error fetching movie data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchMovieData();
    }, [params.id]);

    const formatCurrency = (amount) => {
        if (!amount || amount === 0) return 'Неизвестно';
        return new Intl.NumberFormat('ru-RU').format(amount) + ' $';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Неизвестно';
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const settings = {
        dots: true,
        infinite: true,
        slidesToShow: 5,
        slidesToScroll: 2,
        autoplay: false,
        speed: 500,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 4,
                    slidesToScroll: 2,
                }
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1,
                }
            },
            {
                breakpoint: 480,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                }
            }
        ]
    };

    if (loading) {
        return (
            <div className="film-details-page">
                <Header />
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Загрузка информации о фильме...</p>
                </div>
                <Footer />
            </div>
        );
    }

    if (!movie) {
        return (
            <div className="film-details-page">
                <Header />
                <div className="container-xl">
                    <div className="no-movie">Фильм не найден</div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="film-details-page">
            <Header />
            <div 
                className="movie-hero"
                style={{
                    backgroundImage: movie.backdrop_path 
                        ? `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${IMAGE_BASE_URL}/w1280${movie.backdrop_path})`
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                }}
            >
                <div className="container-xl">
                    <div className="movie-hero-content">
                        <div className="movie-poster-section">
                            <img
                                className="movie-poster-large"
                                src={movie.poster_path ? `${IMAGE_BASE_URL}/w342${movie.poster_path}` : '/images/placeholder.jpg'}
                                alt={movie.title}
                            />
                        </div>
                        <div className="movie-info-section">
                            <h1 className="movie-title">{movie.title}</h1>
                            {movie.original_title !== movie.title && (
                                <h3 className="movie-original-title">{movie.original_title}</h3>
                            )}
                            
                            <div className="movie-meta">
                                <span className="movie-rating">
                                    ⭐ {movie.vote_average?.toFixed(1)} / 10
                                </span>
                                <span className="movie-votes">
                                    ({movie.vote_count?.toLocaleString()} оценок)
                                </span>
                                {movie.runtime && (
                                    <span className="movie-runtime">
                                        ⏱ {movie.runtime} мин
                                    </span>
                                )}
                                {movie.release_date && (
                                    <span className="movie-date">
                                        📅 {new Date(movie.release_date).getFullYear()}
                                    </span>
                                )}
                            </div>

                            {movie.tagline && (
                                <p className="movie-tagline">"{movie.tagline}"</p>
                            )}

                            {movie.genres && movie.genres.length > 0 && (
                                <div className="movie-genres">
                                    {movie.genres.map(genre => (
                                        <span key={genre.id} className="genre-badge">
                                            {genre.name}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {movie.overview && (
                                <div className="movie-overview">
                                    <h3>Описание</h3>
                                    <p>{movie.overview}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="container-xl mt-5">
                {videos.length > 0 && (
                    <section className="trailers-section">
                        <h2 className="section-title">
                            <span className="title-icon">🎬</span>
                            Трейлеры
                        </h2>
                        <div className="trailers-grid">
                            {videos.slice(0, 3).map(video => (
                                <div key={video.id} className="trailer-card">
                                    <div 
                                        className="trailer-thumbnail"
                                        onClick={() => setSelectedVideo(video)}
                                    >
                                        <img
                                            src={`https://img.youtube.com/vi/${video.key}/hqdefault.jpg`}
                                            alt={video.name}
                                        />
                                        <div className="play-button">
                                            <span>▶</span>
                                        </div>
                                    </div>
                                    <h4 className="trailer-title">{video.name}</h4>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {selectedVideo && (
                    <div className="video-modal" onClick={() => setSelectedVideo(null)}>
                        <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
                            <button className="close-button" onClick={() => setSelectedVideo(null)}>
                                ×
                            </button>
                            <iframe
                                className="video-iframe"
                                src={`https://www.youtube.com/embed/${selectedVideo.key}?autoplay=1`}
                                title={selectedVideo.name}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                    </div>
                )}

                {(cast.length > 0 || crew.length > 0) && (
                    <section className="cast-crew-section">
                        <h2 className="section-title">
                            <span className="title-icon">👥</span>
                            Актеры и создатели
                        </h2>
                        {cast.length > 0 && (
                            <div className="cast-container">
                                <h3 className="subsection-title">В ролях</h3>
                                <div className="cast-grid">
                                    {cast.map(actor => (
                                        <div key={actor.id} className="cast-item">
                                            <img
                                                src={actor.profile_path ? `${IMAGE_BASE_URL}/w185${actor.profile_path}` : '/images/placeholder.jpg'}
                                                alt={actor.name}
                                                className="cast-photo"
                                            />
                                            <div className="cast-info">
                                                <div className="cast-name">{actor.name}</div>
                                                <div className="cast-character">{actor.character}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {crew.length > 0 && (
                            <div className="crew-container">
                                <h3 className="subsection-title">Создатели</h3>
                                <div className="crew-grid">
                                    {crew.map(person => (
                                        <div key={`${person.id}-${person.job}`} className="crew-item">
                                            <div className="crew-name">{person.name}</div>
                                            <div className="crew-job">{person.job}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>
                )}

                <section className="details-section">
                    <h2 className="section-title">
                        <span className="title-icon">ℹ️</span>
                        Детальная информация
                    </h2>
                    <div className="details-grid">
                        <div className="detail-item">
                            <span className="detail-label">Дата выхода:</span>
                            <span className="detail-value">{formatDate(movie.release_date)}</span>
                        </div>
                        {movie.production_countries && movie.production_countries.length > 0 && (
                            <div className="detail-item">
                                <span className="detail-label">Страны:</span>
                                <span className="detail-value">
                                    {movie.production_countries.map(c => c.name).join(', ')}
                                </span>
                            </div>
                        )}
                        {movie.production_companies && movie.production_companies.length > 0 && (
                            <div className="detail-item">
                                <span className="detail-label">Производство:</span>
                                <span className="detail-value">
                                    {movie.production_companies.map(c => c.name).join(', ')}
                                </span>
                            </div>
                        )}
                        <div className="detail-item">
                            <span className="detail-label">Бюджет:</span>
                            <span className="detail-value">{formatCurrency(movie.budget)}</span>
                        </div>
                        <div className="detail-item">
                            <span className="detail-label">Сборы:</span>
                            <span className="detail-value">{formatCurrency(movie.revenue)}</span>
                        </div>
                        {movie.spoken_languages && movie.spoken_languages.length > 0 && (
                            <div className="detail-item">
                                <span className="detail-label">Языки:</span>
                                <span className="detail-value">
                                    {movie.spoken_languages.map(l => l.name).join(', ')}
                                </span>
                            </div>
                        )}
                    </div>
                </section>

                {recommendations.length > 0 && (
                    <section className="recommendations-section">
                        <h2 className="section-title">
                            <span className="title-icon">🎯</span>
                            Рекомендации
                        </h2>
                        <Slider {...settings}>
                            {recommendations.map(rec => (
                                <div key={rec.id} className="recommendation-slide">
                                    <Link to={`/film/id/${rec.id}`} className="recommendation-card">
                                        <img
                                            src={rec.poster_path ? `${IMAGE_BASE_URL}/w342${rec.poster_path}` : '/images/placeholder.jpg'}
                                            alt={rec.title}
                                            className="recommendation-poster"
                                        />
                                        <div className="recommendation-overlay">
                                            <div className="recommendation-rating">
                                                ⭐ {rec.vote_average?.toFixed(1)}
                                            </div>
                                            <h4 className="recommendation-title">{rec.title}</h4>
                                        </div>
                                    </Link>
                                </div>
                            ))}
                        </Slider>
                    </section>
                )}
            </div>
            <Footer />
        </div>
    );
};

export default FilmDetails;
